// src/pages/shop/PurchaseReturnIndex.js
import React, { useState, useEffect } from "react";
import useFetchObjects from "../../api/useFetchObjects";
import { useAuth } from "../../context/AuthContext";
import axios from "axios";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Button,
  TextField,
  InputAdornment,
  Collapse,
  IconButton,
  Pagination,
  MenuItem,
  FormControl,
  Select,
  Grid,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
} from "@mui/material";
import {
  Search as SearchIcon,
  KeyboardArrowDown as ExpandIcon,
  KeyboardArrowUp as CollapseIcon,
  Delete as DeleteIcon,
  Print as PrintIcon,
  WarningAmber as WarningIcon,
} from "@mui/icons-material";
import Skeleton from "@mui/material/Skeleton";
import ErrorPage from "../Components/ErrorPage";
import PurchaseReturnAdd from "./PurchaseReturnAdd";
import useDelete from "../../api/useDelete";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { printPurchaseReturn } from "../utils/printPurchaseReturn";

const PurchaseReturnIndex = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token;

  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [expandedRow, setExpandedRow] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, id: null });
  const [notification, setNotification] = useState({ open: false, message: "", severity: "success" });

  // dialogs
  const [openAdd, setOpenAdd] = useState(false);

  const {
    data,
    isLoading,
    isError,
    refetch,
  } = useFetchObjects(
    ["purchase-return", searchTerm, page, rowsPerPage],
    `shop/purchase-returns/?search=${searchTerm}&page=${page}&page_size=${rowsPerPage}`,
    token
  );

  useEffect(() => {
    refetch();
  }, [searchTerm, page, rowsPerPage, refetch]);

  const { handleDelete, ConfirmDialog } = useDelete("shop/purchase-returns", token);

  const handleDeleteClick = (id) => {
    setDeleteDialog({ open: true, id });
  };

  const confirmDelete = async () => {
    try {
      await axios.delete(
        `${process.env.REACT_APP_API_BASE_URL}/shop/purchase-returns/${deleteDialog.id}/`,
        { headers: { Authorization: `Token ${token}` } }
      );
      setNotification({ open: true, message: "Purchase return deleted and stock restored!", severity: "success" });
      refetch();
    } catch (error) {
      setNotification({ open: true, message: "Failed to delete purchase return", severity: "error" });
    }
    setDeleteDialog({ open: false, id: null });
  };

  const purchaseReturns = data && data.results ? data.results : data || [];
  const totalCount = data && data.count ? data.count : purchaseReturns.length;

  const toggleExpandRow = (id) =>
    setExpandedRow((prev) => (prev === id ? null : id));

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setPage(1);
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (e) => {
    setRowsPerPage(Number(e.target.value));
    setPage(1);
  };

  if (isError) {
    return <ErrorPage message={t("purchaseReturn.failedToLoad")} />;
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* ── header ─────────────────────────────────────────── */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "stretch", sm: "center" },
          mb: 2,
          gap: 2,
        }}
      >
        <Typography variant="h4">{t("PurchaseReturnIndex.index_title")}</Typography>

        <Link to="/shop/purchase-return-add">
          <Button variant="contained" color="primary">
            {t("PurchaseReturnIndex.index_addNew")}
          </Button>
        </Link>
      </Box>

      <Typography variant="subtitle1" color="textSecondary" mb={2}>
        {t("shop.dataLoadedOn")} {new Date().toLocaleString()}
      </Typography>

      {/* ── search ─────────────────────────────────────────── */}
      <Box sx={{ mb: 2 }}>
        <TextField
          variant="outlined"
          fullWidth
          placeholder={t("PurchaseReturnIndex.index_search")}
          value={searchTerm}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* ── table ──────────────────────────────────────────── */}
      <TableContainer component={Paper} sx={{ mt: 2 }}>
        <Table sx={{ minWidth: 650 }} aria-label="purchase return table">
          <TableHead>
            <TableRow sx={{ backgroundColor: "primary.main" }}>
              <TableCell sx={{ color: "white" }} />
              <TableCell sx={{ color: "white" }}>
                {t("PurchaseReturnIndex.table_id")}
              </TableCell>
              <TableCell sx={{ color: "white" }}>
                {t("shop.shop")}
              </TableCell>
              <TableCell sx={{ color: "white" }}>
                {t("PurchaseReturnIndex.table_purchase")}
              </TableCell>
              <TableCell sx={{ color: "white" }}>
                {t("PurchaseReturnIndex.table_refundTotal")}
              </TableCell>
              <TableCell sx={{ color: "white" }}>
                {t("PurchaseReturnIndex.table_returnDate")}
              </TableCell>
              <TableCell sx={{ color: "white" }}>
                {t("PurchaseReturnIndex.table_reason")}
              </TableCell>
              <TableCell sx={{ color: "white" }}>
                Actions
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {isLoading
              ? Array.from({ length: 5 }).map((_, r) => (
                  <TableRow key={`sk-${r}`}>
                    {Array.from({ length: 8 }).map((_, c) => (
                      <TableCell key={`sk-${r}-${c}`}>
                        <Skeleton variant="text" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              : purchaseReturns.map((purchaseReturn, index) => {
                  const totalRefund = purchaseReturn.return_items
                    ?.reduce((sum, item) => {
                      const val =
                        item.calculated_refund_amount?.parsedValue ??
                        parseFloat(item.refund_amount) ??
                        0;
                      return sum + val;
                    }, 0)
                    .toFixed(2) || "0.00";

                  const purchaseId = purchaseReturn.purchase || purchaseReturn.purchase_details?.id || 'N/A';

                  return (
                    <React.Fragment key={purchaseReturn.id}>
                      {/* summary row */}
                      <TableRow hover>
                        <TableCell>
                          <IconButton
                            size="small"
                            onClick={() => toggleExpandRow(purchaseReturn.id)}
                          >
                            {expandedRow === purchaseReturn.id ? (
                              <CollapseIcon />
                            ) : (
                              <ExpandIcon />
                            )}
                          </IconButton>
                        </TableCell>

                        <TableCell>{(page - 1) * rowsPerPage + index + 1}</TableCell>
                        <TableCell>{purchaseReturn.shop_details?.name || t("shop.unknownShop")}</TableCell>
                        <TableCell>
                          <Chip 
                            label={`Purchase #${purchaseReturn.purchase || purchaseReturn.id}`} 
                            color="primary" 
                            size="small" 
                            variant="outlined"
                            component={Link}
                            to={`/shop/purchases`}
                            clickable
                          />
                        </TableCell>
                        <TableCell>
                          {totalRefund}
                        </TableCell>
                        <TableCell>
                          {new Date(purchaseReturn.return_date || purchaseReturn.created_at).toLocaleString()}
                        </TableCell>
                        <TableCell>{purchaseReturn.reason || "N/A"}</TableCell>
                        <TableCell>
                          <IconButton
                            size="small"
                            onClick={() => printPurchaseReturn(purchaseReturn)}
                            color="primary"
                            sx={{ mr: 1 }}
                          >
                            <PrintIcon />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteClick(purchaseReturn.id)}
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>

                      {/* expanded detail row */}
                      <TableRow>
                        <TableCell
                          style={{ paddingBottom: 0, paddingTop: 0 }}
                          colSpan={8}
                        >
                          <Collapse
                            in={expandedRow === purchaseReturn.id}
                            timeout="auto"
                            unmountOnExit
                          >
                            <Box sx={{ m: 1 }}>
                              <Typography
                                variant="subtitle2"
                                gutterBottom
                                component="div"
                              >
                                {t("PurchaseReturnIndex.table_itemCount")} (
                                {purchaseReturn.return_items?.length || 0})
                              </Typography>

                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell>
                                      {t("PurchaseReturnIndex.table_product")}
                                    </TableCell>
                                    <TableCell align="right">
                                      {t("PurchaseReturnIndex.table_price")}
                                    </TableCell>
                                    <TableCell align="right">
                                      {t("PurchaseReturnIndex.table_returnQty")}
                                    </TableCell>
                                    <TableCell align="right">
                                      {t("PurchaseReturnIndex.table_refundAmount")}
                                    </TableCell>
                                    <TableCell>
                                      {t("PurchaseReturnIndex.table_returnDate")}
                                    </TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {purchaseReturn.return_items?.map((item) => {
                                    const itemRefund =
                                      item.calculated_refund_amount
                                        ?.parsedValue ??
                                      parseFloat(item.refund_amount) ??
                                      0;

                                    return (
                                      <TableRow key={item.id}>
                                        <TableCell>
                                          {
                                            item.purchase_item_details?.product_details?.name ||
                                            item.purchase_item_details?.product?.name ||
                                            "N/A"
                                          }
                                        </TableCell>
                                        <TableCell align="right">
                                          {item.purchase_item_details?.buy_price ||
                                           item.purchase_item_details?.purchase_price ||
                                           "0.00"}
                                        </TableCell>
                                        <TableCell align="right">
                                          {item.return_quantity}
                                        </TableCell>
                                        <TableCell align="right">
                                          {itemRefund.toFixed(2)}
                                        </TableCell>
                                        <TableCell>
                                          {new Date(
                                            item.created_at
                                          ).toLocaleString()}
                                        </TableCell>
                                      </TableRow>
                                    );
                                  }) || []}
                                </TableBody>
                              </Table>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  );
                })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <Grid
        container
        justifyContent="center"
        alignItems="center"
        padding="10px"
      >
        <Box display="flex" alignItems="center" gap={1}>
          <Typography>{t("purchaseReturn.rowsPerPage", "Rows per page:")}</Typography>
          <FormControl
            sx={{
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  border: "none",
                },
              },
            }}
            size="small"
          >
            <Select value={rowsPerPage} onChange={handleRowsPerPageChange}>
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={20}>20</MenuItem>
              <MenuItem value={30}>30</MenuItem>
            </Select>
          </FormControl>
        </Box>
        <Pagination
          count={Math.ceil(totalCount / rowsPerPage)}
          page={page}
          onChange={handlePageChange}
          variant="outlined"
          color="primary"
          sx={{ ml: 2 }}
        />
      </Grid>

      {/* dialogs */}
      {openAdd && (
        <PurchaseReturnAdd
          open={openAdd}
          onClose={() => setOpenAdd(false)}
          token={token}
          owner={auth?.user?.user?.id}
        />
      )}
      
      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, id: null })}>
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <WarningIcon color="error" />
            Confirm Delete
          </Box>
        </DialogTitle>
        <DialogContent>
          Are you sure you want to delete this purchase return? The returned quantities will be added back to stock.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, id: null })}>Cancel</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Notification */}
      <Snackbar
        open={notification.open}
        autoHideDuration={3000}
        onClose={() => setNotification({ ...notification, open: false })}
      >
        <Alert severity={notification.severity}>
          {notification.message}
        </Alert>
      </Snackbar>
      
      <ConfirmDialog />
    </Box>
  );
};

export default PurchaseReturnIndex;